import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import type { AddWellsColumnKey } from './add-wells-utils';
import styles from './add-wells-tearsheet.module.scss';

export interface AddWellsRow {
  uwi: string;
  wellName: string;
  operator: string;
  status: string;
  /** Already in the reconcile pool: shown as "Added" and locked. */
  alreadyAdded: boolean;
}

export interface AddWellsResultsTableProps {
  rows: AddWellsRow[];
  selected: Set<string>;
  allSelected: boolean;
  /** Whether any row is addable, so the select-all can be disabled when none is. */
  anyAddable: boolean;
  /** Columns the viewer has hidden from the Show/Hide Columns menu. */
  hiddenColumns: ReadonlySet<AddWellsColumnKey>;
  onToggle: (uwi: string) => void;
  onToggleAll: (checked: boolean) => void;
}

/**
 * The catalog search results as a multi-select table. A row already in the reconcile is shown as
 * "Added" and locked. Presentational — the selection lives in the tearsheet above it.
 * @param {AddWellsResultsTableProps} props The rows, the selection and its handlers.
 * @return {JSX.Element} The results table.
 */
export function AddWellsResultsTable({
  rows,
  selected,
  allSelected,
  anyAddable,
  hiddenColumns,
  onToggle,
  onToggleAll,
}: AddWellsResultsTableProps) {
  return (
    <div className={styles.tableWrap}>
      <Table size="md">
        <TableHead>
          <TableRow>
            <TableHeader className={styles.checkCell}>
              <Checkbox
                id="add-wells-select-all"
                labelText="Select every matching well"
                hideLabel
                checked={allSelected}
                disabled={!anyAddable}
                onChange={(
                  _event: unknown,
                  { checked }: { checked: boolean }
                ) => onToggleAll(checked)}
              />
            </TableHeader>
            {!hiddenColumns.has('wellName') && (
              <TableHeader>Well Name</TableHeader>
            )}
            {!hiddenColumns.has('uwi') && <TableHeader>UWI</TableHeader>}
            {!hiddenColumns.has('operator') && (
              <TableHeader>Operator</TableHeader>
            )}
            {!hiddenColumns.has('status') && (
              <TableHeader>Well Status</TableHeader>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.uwi}>
              <TableCell className={styles.checkCell}>
                <Checkbox
                  id={`add-wells-check-${row.uwi}`}
                  labelText={
                    row.alreadyAdded
                      ? `${row.wellName} is already in the reconcile`
                      : `Add ${row.wellName}`
                  }
                  hideLabel
                  checked={row.alreadyAdded || selected.has(row.uwi)}
                  disabled={row.alreadyAdded}
                  onChange={() => onToggle(row.uwi)}
                />
              </TableCell>
              {!hiddenColumns.has('wellName') && (
                <TableCell>{row.wellName}</TableCell>
              )}
              {!hiddenColumns.has('uwi') && <TableCell>{row.uwi}</TableCell>}
              {!hiddenColumns.has('operator') && (
                <TableCell>{row.operator}</TableCell>
              )}
              {!hiddenColumns.has('status') && (
                <TableCell>
                  {row.alreadyAdded ? (
                    <span className={styles.added}>Added</span>
                  ) : (
                    row.status
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
